import React from "react";
import { cn } from "@/lib/utils";
import { ImagePlus, Video, Image } from "lucide-react";
import type { CellProps } from "../types";

interface CreativeValue {
  id?: string;
  thumbnailUrl?: string;
  type?: "image" | "video";
  filename?: string;
}

interface CreativeCellProps extends Omit<CellProps<CreativeValue | null>, "onChange"> {
  onChange: (value: CreativeValue | null) => void;
  verticalValue?: CreativeValue | null;
  disabled?: boolean;
  /** When true, only show a single asset slot with no dimension labels */
  singleAssetOnly?: boolean;
}

export function CreativeCell({ value, onChange, verticalValue, disabled, singleAssetOnly }: CreativeCellProps) {
  const hasFeed = !!value?.thumbnailUrl;
  const hasVertical = !singleAssetOnly && !!verticalValue?.thumbnailUrl;
  const hasBoth = hasFeed && hasVertical;

  return (
    <button
      type="button"
      onClick={() => onChange(null)}
      disabled={disabled}
      className={cn(
        "flex items-center justify-center w-full h-full rounded",
        "border border-transparent transition-colors",
        "hover:border-border hover:bg-accent/50",
        "focus:outline-none focus:border-primary",
        "disabled:opacity-50 disabled:cursor-not-allowed"
      )}
    >
      {hasBoth ? (
        <div className="flex items-center justify-center gap-2">
          <img
            src={value.thumbnailUrl!}
            alt="Feed"
            className="h-20 w-20 rounded-md object-cover border border-border"
          />
          <img
            src={verticalValue!.thumbnailUrl!}
            alt="Vertical"
            className="h-20 w-14 rounded-md object-cover border border-border"
          />
        </div>
      ) : hasFeed ? (
        <img
          src={value.thumbnailUrl!}
          alt="Creative"
          className="h-20 w-20 rounded-md object-cover border border-border"
        />
      ) : (
        <div className="h-20 w-20 rounded-md bg-muted flex items-center justify-center border border-dashed border-muted-foreground/30">
          <ImagePlus className="h-6 w-6 text-muted-foreground" />
        </div>
      )}
    </button>
  );
}